import React, { useEffect, useId, useRef, useState } from "react";
import styles from "@/pages/accounts.module.scss";

type DetailFieldProps = {
  icon: string;
  label: string;
  children: React.ReactNode;
  /** Click-to-reveal help for the field (shown via info icon). */
  infoTip?: string | null;
  /** Optional small action rendered inline after the label (e.g. a pencil). */
  labelAction?: React.ReactNode;
  className?: string;
};

/** LABEL-above-value field with optional Bootstrap Icon. */
export function DetailField({
  icon,
  label,
  children,
  infoTip,
  labelAction,
  className,
}: DetailFieldProps) {
  const tipId = useId();
  const [tipOpen, setTipOpen] = useState(false);
  const tipWrapRef = useRef<HTMLDivElement | null>(null);
  const tipText = String(infoTip || "").trim();

  useEffect(() => {
    if (!tipOpen) return;
    const onDoc = (event: MouseEvent) => {
      if (!tipWrapRef.current?.contains(event.target as Node)) {
        setTipOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTipOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [tipOpen]);

  return (
    <div className={[styles.detailField, className].filter(Boolean).join(" ")}>
      <div className={styles.detailFieldIconCol} aria-hidden="true">
        <i className={`${icon} ${styles.detailFieldIcon}`} />
      </div>
      <div className={styles.detailFieldBody}>
        <div className={styles.detailFieldLabel}>
          <span>{label}</span>
          {tipText ? (
            <div className={styles.detailFieldInfoWrap} ref={tipWrapRef}>
              <button
                type="button"
                className={styles.detailFieldInfoBtn}
                aria-label={`About ${label}`}
                aria-expanded={tipOpen}
                aria-controls={tipId}
                onClick={() => setTipOpen((open) => !open)}
                data-testid={`detail-field-info-${label}`}
              >
                <i className="bi bi-info-circle" aria-hidden="true" />
              </button>
              {tipOpen ? (
                <div
                  id={tipId}
                  role="tooltip"
                  className={styles.detailFieldInfoTip}
                >
                  {tipText}
                </div>
              ) : null}
            </div>
          ) : null}
          {labelAction ? (
            <span className={styles.detailFieldLabelAction}>{labelAction}</span>
          ) : null}
        </div>
        <div className={styles.detailFieldValue}>{children}</div>
      </div>
    </div>
  );
}

type DetailSectionProps = {
  title: string;
  /** Bootstrap Icon class shown before the section title. */
  icon?: string;
  /** Optional right-aligned content in the section header strip. */
  headerRight?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
};

/** Card-style section: gray header strip (icon + title [+ right slot]) over a padded body. */
export function DetailSection({
  title,
  icon,
  headerRight,
  children,
  className,
  "data-testid": testId,
}: DetailSectionProps) {
  return (
    <section
      className={[styles.detailsSection, className].filter(Boolean).join(" ")}
      data-testid={testId}
    >
      <div className={styles.detailsSectionHeader}>
        <h6 className={styles.detailsSectionTitle}>
          {icon ? (
            <i
              className={`${icon} ${styles.detailsSectionIcon}`}
              aria-hidden="true"
            />
          ) : null}
          <span>{title}</span>
        </h6>
        {headerRight ? (
          <div className={styles.detailsSectionHeaderRight}>{headerRight}</div>
        ) : null}
      </div>
      <div className={styles.detailsSectionBody}>{children}</div>
    </section>
  );
}

export function DetailGrid({ children }: { children: React.ReactNode }) {
  return <div className={styles.detailsGrid}>{children}</div>;
}
